import logger from "@/lib/logger";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Loader2, Printer, Receipt, Search, ChevronLeft, ChevronRight, Plus, X, Trash2 } from "lucide-react";
import { MONTHS, YEARS, BODY_TYPES } from "@/pages/NewJob";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const fmtDate = d => d ? new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }) : "";

export default function Billing() {
  const navigate = useNavigate();

  // Form
  const now = new Date();
  const [customer,  setCustomer]  = useState("");
  const [product,   setProduct]   = useState("");
  const [quantity,  setQuantity]  = useState("");
  const [month,     setMonth]     = useState(now.getMonth() + 1);
  const [year,      setYear]      = useState(now.getFullYear());
  const [bodyType,  setBodyType]  = useState("");
  const [creating,  setCreating]  = useState(false);
  const [errors,    setErrors]    = useState({});
  const [lastBill, setLastBill] = useState(null); // show success result

  // List
  const [bills,    setBills]    = useState([]);
  const [total,    setTotal]    = useState(0);
  const [pages,    setPages]    = useState(1);
  const [page,     setPage]     = useState(1);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [searchIn, setSearchIn] = useState("");

  // Multi-select + delete
  const [selected,       setSelected]       = useState(new Set());
  const [showDelConfirm, setShowDelConfirm] = useState(false);
  const [deleting,       setDeleting]       = useState(false);

  const fetchBills = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (search) params.search = search;
      const res = await api.get(`/bills`, { params });
      setBills(res.data.bills || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (err) {
      logger.error("[Billing] fetchBills:", err?.message);
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]); // `api` is a stable module-level import — safe to omit

  useEffect(() => { fetchBills(); }, [fetchBills]);

  const toggleSelect = (id) => setSelected(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll    = () => { if (selected.size === bills.length) setSelected(new Set()); else setSelected(new Set(bills.map(b => b.id))); };

  const deleteSelected = async () => {
    setDeleting(true);
    try {
      const res = await api.delete(`${API}/bills/bulk`, { data: { ids: [...selected] } });
      toast.success(`Deleted ${res.data.deleted} bill${res.data.deleted !== 1 ? "s" : ""}`);
      setSelected(new Set()); setShowDelConfirm(false); fetchBills();
    } catch (err) {
      logger.error("[Billing] deleteSelected:", err?.message);
      toast.error("Failed to delete bills");
    } finally { setDeleting(false); }
  };

  const validate = () => {
    const e = {};
    if (!customer.trim())           e.customer = "Customer name is required";
    if (!product.trim())            e.product  = "Product name is required";
    if (!quantity || parseInt(quantity) < 1)  e.quantity = "Quantity must be at least 1";
    if (parseInt(quantity) > 5000)  e.quantity = "Maximum 5000 pieces per bill";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setCreating(true);
    try {
      const res = await api.post(`/bills`, {
        customer_name: customer.trim(),
        product_name:  product.trim(),
        quantity:      parseInt(quantity),
        month,
        year,
        body_type:     bodyType,
      });
      setLastBill(res.data);
      toast.success(`Bill ${res.data.bill_no} created — ${res.data.quantity} barcodes generated!`);
      setCustomer(""); setProduct(""); setQuantity("");
      fetchBills();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create bill");
    } finally { setCreating(false); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-green-600 flex items-center justify-center">
          <Receipt size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Billing</h1>
          <p className="text-sm text-slate-500 mt-0.5">Create a bill and generate unique barcodes for every piece</p>
        </div>
      </div>

      {/* Create Form */}
      <Card className="rounded-2xl border border-slate-200/70 bg-white mb-6">
        <CardContent className="p-6">
          <h2 className="text-sm font-semibold text-slate-900 mb-5" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>New Bill</h2>
          <form onSubmit={handleCreate}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
              <div>
                <Label className="text-xs font-medium text-slate-700">Customer Name <span className="text-rose-500">*</span></Label>
                <Input
                  placeholder="e.g. Emmy"
                  value={customer}
                  onChange={e => setCustomer(e.target.value)}
                  className={`mt-1.5 h-10 text-sm ${errors.customer ? "border-rose-400" : ""}`}
                  data-testid="bill-customer-input"
                />
                {errors.customer && <p className="text-xs text-rose-500 mt-1">{errors.customer}</p>}
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-700">Product Name <span className="text-rose-500">*</span></Label>
                <Input
                  placeholder="e.g. EV Charger"
                  value={product}
                  onChange={e => setProduct(e.target.value)}
                  className={`mt-1.5 h-10 text-sm ${errors.product ? "border-rose-400" : ""}`}
                  data-testid="bill-product-input"
                />
                {errors.product && <p className="text-xs text-rose-500 mt-1">{errors.product}</p>}
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-700">Quantity (pieces) <span className="text-rose-500">*</span></Label>
                <Input
                  type="number" min="1" max="5000" placeholder="e.g. 100"
                  value={quantity}
                  onChange={e => setQuantity(e.target.value)}
                  className={`mt-1.5 h-10 text-sm font-semibold ${errors.quantity ? "border-rose-400" : ""}`}
                  data-testid="bill-quantity-input"
                />
                {errors.quantity && <p className="text-xs text-rose-500 mt-1">{errors.quantity}</p>}
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-700">Purchase Month</Label>
                <Select value={String(month)} onValueChange={v => setMonth(parseInt(v))}>
                  <SelectTrigger className="mt-1.5 h-10 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {MONTHS.map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-700">Purchase Year</Label>
                <Select value={String(year)} onValueChange={v => setYear(parseInt(v))}>
                  <SelectTrigger className="mt-1.5 h-10 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-700">Body Type</Label>
                <Select value={bodyType} onValueChange={setBodyType}>
                  <SelectTrigger className="mt-1.5 h-10 text-sm"><SelectValue placeholder="Select body" /></SelectTrigger>
                  <SelectContent>
                    {BODY_TYPES.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Preview */}
            {customer && product && parseInt(quantity) > 0 && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-xl mb-5 text-xs">
                <p className="font-semibold text-green-700 mb-1">Preview — what will be generated:</p>
                <p className="text-slate-700">
                  <strong>{parseInt(quantity)}</strong> barcodes &nbsp;·&nbsp;
                  <strong>{customer}</strong> &nbsp;·&nbsp;
                  <strong>{product}</strong>
                  {bodyType ? ` · ${bodyType}` : ""}
                  {` · ${MONTHS[month-1]} ${year}`}
                </p>
                <p className="font-mono text-green-800 mt-1">
                  First: BL-XXXX-0001 &nbsp;→&nbsp; Last: BL-XXXX-{String(parseInt(quantity)||1).padStart(4, "0")}
                </p>
              </div>
            )}

            <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white" disabled={creating} data-testid="bill-submit-btn">
              {creating ? <><Loader2 size={16} className="mr-2 animate-spin" />Creating...</> : <><Plus size={16} className="mr-2" />Create Bill &amp; Generate Barcodes</>}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Last created bill — success card */}
      {lastBill && (
        <Card className="rounded-2xl border-2 border-green-400 bg-green-50 mb-6">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-green-600 font-semibold uppercase tracking-wide mb-2">✅ Bill Created Successfully</p>
                <p className="font-mono text-2xl font-extrabold text-green-700 tracking-tight">{lastBill.bill_no}</p>
                <p className="text-sm text-slate-700 mt-1">
                  <strong>{lastBill.customer_name}</strong> · {lastBill.product_name} · <strong>{lastBill.quantity} pcs</strong>
                  {lastBill.body_type ? ` · ${lastBill.body_type}` : ""}
                  {lastBill.month ? ` · ${MONTH_NAMES[lastBill.month-1]} ${lastBill.year}` : ""}
                </p>
                <div className="mt-3 space-y-1">
                  <p className="text-xs text-slate-500">Barcode range:</p>
                  <p className="font-mono text-sm font-bold text-green-700">
                    {lastBill.barcode_start} &nbsp;→&nbsp; {lastBill.barcode_end}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 ml-4">
                <a href={`/bill-labels/${lastBill.id}`} target="_blank" rel="noreferrer">
                  <Button className="bg-green-600 hover:bg-green-700 text-white" data-testid="bill-print-labels-btn">
                    <Printer size={16} className="mr-2" /> Print {lastBill.quantity} Labels
                  </Button>
                </a>
                <button onClick={() => setLastBill(null)} className="text-slate-400 hover:text-slate-600 p-2">
                  <X size={16} />
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bills List */}
      <Card className="rounded-2xl border border-slate-200/70 bg-white">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
              All Bills <span className="text-slate-400 font-normal">({total})</span>
            </h2>
            {selected.size > 0 && (
              <Button variant="outline" size="sm" className="h-7 text-xs border-rose-200 text-rose-600 hover:bg-rose-50"
                onClick={() => setShowDelConfirm(true)} data-testid="bill-bulk-delete-button">
                <Trash2 size={12} className="mr-1" /> Delete Selected ({selected.size})
              </Button>
            )}
          </div>
          <form className="flex gap-2" onSubmit={e => { e.preventDefault(); setSearch(searchIn); setPage(1); }}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input placeholder="Search customer, product, bill #..."
                value={searchIn} onChange={e => setSearchIn(e.target.value)}
                className="pl-8 h-8 text-xs w-56 bg-white"
                data-testid="bill-search-input" />
            </div>
            {search && (
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => { setSearch(""); setSearchIn(""); setPage(1); }}>
                <X size={12} className="mr-1" />Clear
              </Button>
            )}
          </form>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/60">
                <th className="px-4 py-3 w-10">
                  <Checkbox checked={bills.length > 0 && selected.size === bills.length} onCheckedChange={toggleAll} />
                </th>
                {["Bill #","Customer","Product","Body","Month/Year","Qty","Barcode Start → End",""].map(h => (
                  <th key={h} className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? ([...Array(4)].map((_,i) => (
                <tr key={i}>{[...Array(9)].map((_,j) => <td key={j} className="px-4 py-3"><Skeleton className="h-4" /></td>)}</tr>
              ))) : bills.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <Receipt className="w-10 h-10 text-slate-300" />
                    <p className="text-sm text-slate-500">No bills yet. Create your first bill above.</p>
                  </div>
                </td></tr>
              ) : bills.map(bill => (
                <tr key={bill.id} className={`border-b border-slate-50 hover:bg-slate-50/60 transition-colors ${selected.has(bill.id) ? "bg-rose-50/30" : ""}`}>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <Checkbox checked={selected.has(bill.id)} onCheckedChange={() => toggleSelect(bill.id)} />
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm font-bold text-green-700">{bill.bill_no}</span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-slate-900">{bill.customer_name}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-slate-700">{bill.product_name}</p>
                  </td>
                  <td className="px-4 py-3">
                    {bill.body_type
                      ? <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${bill.body_type==="Metal Body"?"bg-slate-100 text-slate-700 border-slate-300":"bg-sky-50 text-sky-700 border-sky-200"}`}>{bill.body_type}</span>
                      : <span className="text-xs text-slate-300">—</span>
                    }
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-700">
                      {bill.month ? `${MONTH_NAMES[bill.month-1]} ${bill.year}` : fmtDate(bill.created_at)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>{bill.quantity}</span>
                    <span className="text-xs text-slate-400 ml-1">pcs</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="font-mono text-xs text-green-700 font-semibold">{bill.barcode_start}</span>
                    <span className="text-slate-300 mx-1">→</span>
                    <span className="font-mono text-xs text-green-700 font-semibold">{bill.barcode_end}</span>
                  </td>
                  <td className="px-4 py-3">
                    <a href={`/bill-labels/${bill.id}`} target="_blank" rel="noreferrer">
                      <Button variant="outline" size="sm" className="h-7 text-xs border-green-200 text-green-700 hover:bg-green-50">
                        <Printer size={12} className="mr-1" /> Print Labels
                      </Button>
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
            <p className="text-xs text-slate-500">{total} total bills</p>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-7 w-7" disabled={page===1} onClick={() => setPage(p=>p-1)}><ChevronLeft size={13}/></Button>
              <span className="text-xs text-slate-600 px-2">Page {page} of {pages}</span>
              <Button variant="outline" size="icon" className="h-7 w-7" disabled={page===pages} onClick={() => setPage(p=>p+1)}><ChevronRight size={13}/></Button>
            </div>
          </div>
        )}
      </Card>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={showDelConfirm} onOpenChange={setShowDelConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} bill{selected.size !== 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{selected.size}</strong> selected bill{selected.size !== 1 ? "s" : ""} and all their barcode data.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700"
              onClick={deleteSelected}
              disabled={deleting}
              data-testid="bill-bulk-delete-confirm"
            >
              {deleting ? <><Loader2 size={13} className="mr-1.5 animate-spin" />Deleting...</> : `Delete ${selected.size} Bill${selected.size !== 1 ? "s" : ""}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
